import { Switch, SwitchChangeEvent } from '@progress/kendo-react-inputs';
import { useCallback, useMemo } from 'react';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import PopUp from 'src/components/design-system/PopUp';
import useConfirm from 'src/hooks/useConfirm';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from './InvoiceDetailsContext';

const AutoDistributeGlCodes = () => {
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const setInvoice = useContextSelector(InvoiceContext, (value) => value.setInvoice);
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const glBreakoutHasBothNegativeAndPositiveValues = useContextSelector(
    InvoiceContext,
    (value) => value.glBreakoutHasBothNegativeAndPositiveValues
  );
  const { confirm } = useConfirm();

  const value = useMemo(() => invoice?.isAutoDistGlBreakout, [invoice?.isAutoDistGlBreakout]);

  const handleChange = useCallback(
    (e: SwitchChangeEvent) => {
      if (e.value) {
        confirm({
          title: 'Auto-distribute GL codes?',
          content: (
            <div>
              <div className="max-w-md mb-4">
                Auto-distribution will reset all discount, shipping and tax amounts assigned to the
                GL Codes, and will remove any GL Codes not assigned to a line item.
              </div>
              <div>Are you sure you want to continue?</div>
            </div>
          ),
          confirmButtonText: 'OK',
        }).then((isAutoDistributeConfirmed) => {
          if (isAutoDistributeConfirmed) {
            setInvoice((prev) => ({ ...prev, isAutoDistGlBreakout: true }));
          }
        });
      } else {
        setInvoice((prev) => ({ ...prev, isAutoDistGlBreakout: false }));
      }
    },
    [confirm, setInvoice]
  );

  return (
    <>
      <div className="whitespace-nowrap text-white">Auto-Distribute</div>
      {!isInvoiceEditDisabled ? (
        <PopUp
          content={
            <PopUpContentWrapper
              type="Error"
              contents={[
                'Use of a negative GL subtotal on an invoice requires manual distribution of Discount, Shipping, and Tax values. Please adjust the GL code(s) with the negative values to restore access to the Auto-Distribute feature.',
              ]}
            />
          }
          show={glBreakoutHasBothNegativeAndPositiveValues}
        >
          <Switch
            checked={value}
            onLabel={'YES'}
            offLabel={'NO'}
            onChange={handleChange}
            disabled={glBreakoutHasBothNegativeAndPositiveValues}
          />
        </PopUp>
      ) : (
        <div className="mx-4 font-bold text-white"> {value ? 'Yes' : 'No'} </div>
      )}
    </>
  );
};

export default AutoDistributeGlCodes;
